import type { CalendarDate } from '@internationalized/date';
import { createContext, useContext, type ReactNode } from 'react';

/** @internal */
export type DatePickerView = 'month' | 'year' | 'multi-year';

interface DatePickerViewState {
  currentView: DatePickerView;
  switchToView: (value: DatePickerView) => void;
  focusedDate: CalendarDate;
  setFocusedDate: (value: CalendarDate) => void;
}

const DatePickerViewContext = createContext<DatePickerViewState | null>(null);

interface DatePickerViewProviderProps extends DatePickerViewState {
  children: ReactNode;
}

/** @internal */
export function DatePickerViewProvider({
  children,
  currentView,
  switchToView,
  focusedDate,
  setFocusedDate,
}: DatePickerViewProviderProps) {
  return (
    <DatePickerViewContext.Provider value={{ currentView, switchToView, focusedDate, setFocusedDate }}>
      {children}
    </DatePickerViewContext.Provider>
  );
}

/** @internal */
// eslint-disable-next-line react-refresh/only-export-components
export function useDatePickerView(): DatePickerViewState {
  const state = useContext(DatePickerViewContext);
  if (!state) {
    throw new Error('Cannot use date picker view state outside of a date picker');
  }

  return state;
}
